import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PrequalificationRole } from "@/api/prequalification";
import { programBrochuresApi } from "@/api/programBrochures";
import { programBrochureKeys } from "./prequalKeys";

// Deal Flow → Prequalification → Program Brochure.
//
// One read for both roles; three mutations, each returning the changed role's
// state and each invalidating the read. Invalidate rather than `setQueryData`
// because the cache holds BOTH roles while a mutation answers for one — patching
// it by hand would be a second place the overview's shape is known.

/** The active brochure and full upload history for both roles. */
export function useProgramBrochures() {
  return useQuery({
    queryKey: programBrochureKeys.overview(),
    queryFn: () => programBrochuresApi.overview(),
  });
}

/** Upload a brochure and make it active, retaining the incumbent as history. */
export function useUploadProgramBrochure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { role: PrequalificationRole; file: File }) =>
      programBrochuresApi.upload(vars.role, vars.file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programBrochureKeys.all });
    },
  });
}

/** Roll a role back to one of its prior uploads. */
export function useRestoreProgramBrochure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { role: PrequalificationRole; id: string }) =>
      programBrochuresApi.restore(vars.role, vars.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programBrochureKeys.all });
    },
  });
}

/** Stop serving a brochure for a role. Nothing is destroyed — the row and blob
 *  stay, and a restore brings them back. */
export function useDeactivateProgramBrochure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { role: PrequalificationRole }) =>
      programBrochuresApi.deactivate(vars.role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programBrochureKeys.all });
    },
  });
}
